import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Spinner,
  Dropdown,
  Row,
  Col,
  Container,
  Tabs,
  Tab,
  Card,
} from "react-bootstrap";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import { useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useTasks from "hooks/tasks/useTasks";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import SubtleBadge from "components/common/SubtleBadge";
import DeleteTaskModal from "./DeleteTaskModal";
import DeactivateTaskModal from "./DeactivateTaskModal";
import TablePaginationFooter from "components/common/TablePaginationFooter";
import { toast } from "react-toastify";
import { ChevronLeft, ChevronRight } from "lucide-react"; 
import { useRef } from "react";
import { Empty, Spin } from "antd";

const TAB_CONFIG = [
  { key: "NEW", label: "New", searchTerm: "NEW" },
  { key: "FLAGGED", label: "Flagged/Blocked", searchTerm: "BLOCKED" },
  {
    key: "IN_PROGRESS",
    label: "In Progress",
    searchTerm: "IN_PROGRESS",
  },
  { key: "COMPLETED", label: "Completed", searchTerm: "COMPLETED" },
  { key: "ACCEPTED", label: "Accepted", searchTerm: "ACCEPTED" },
  { key: "OTW", label: "OTW", searchTerm: "NAVIGATING" },
  { key: "CANCELLED", label: "Cancelled", searchTerm: "CANCELLED" },
  { key: "EXPIRED", label: "Expired", searchTerm: "EXPIRED" },
  { key: "ALL", label: "All Tasks", searchTerm: "" },
];

const STATUS_MAP = {
  NEW: { label: "New", color: "info" },
  PENDING: { label: "Pending", color: "warning" },
  ACCEPTED: { label: "Accepted", color: "success" },
  NAVIGATING: { label: "Navigating", color: "primary" },
  IN_PROGRESS: { label: "In Progress", color: "primary" },
  ARRIVED: { label: "Arrived", color: "success" },
  IN_REVIEW: { label: "In Review", color: "info" },
  COMPLETED: { label: "Completed", color: "success" },
  CANCELLED: { label: "Cancelled", color: "danger" },
  EXPIRED: { label: "Expired", color: "secondary" },
  DEACTIVATED: { label: "Deactivated", color: "secondary" },
  DELETED: { label: "Deleted", color: "dark" },
  FLAGGED: { label: "Flagged", color: "warning" },
  BLOCKED: { label: "Blocked", color: "danger" },
  OTW: { label: "OTW", color: "info" },
};

const Tasks = () => {
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [activeTab, setActiveTab] = useState("NEW");
  const [taskPendingDelete, setTaskPendingDelete] = useState(null);
  const [taskPendingDeactivate, setTaskPendingDeactivate] = useState(null);
  const [processing, setProcessing] = useState(false);
  const scrollRef = useRef(null);

  const currentTab = useMemo(
    () => TAB_CONFIG.find((tab) => tab.key === activeTab),
    [activeTab],
  );

  const scroll = (direction) => {
  if (scrollRef.current) {
    const { scrollLeft, clientWidth } = scrollRef.current;
    const scrollTo = direction === "left" ? scrollLeft - 200 : scrollLeft + 200;
    scrollRef.current.scrollTo({ left: scrollTo, behavior: "smooth" });
    }
  };


  const {
    tasks,
    loading,
    error,
    getTaskById,
    fetchTasks,
    deactivateTask,
    deleteTask,
    totalPages,
    totalElements,
  } = useTasks();

  useEffect(() => {
    fetchTasks(page, pageSize, currentTab?.searchTerm || "");
  }, [page, pageSize, activeTab, fetchTasks]);

  const mappedTasks = useMemo(() => {
    return tasks.map((task) => {
      const requester = task.requester;
      const helper = task.helper;

      const requesterName = requester
        ? `${requester.firstName || ""} ${requester.lastName || ""}`
            .trim()
            .replace(/\s+/g, " ")
        : "N/A";

      const helperName = helper
        ? `${helper.firstName || ""} ${helper.lastName || ""}`
            .trim()
            .replace(/\s+/g, " ")
        : "N/A";

      return {
        id: task.id,
        title: task.title,
        requesterName,
        helperName,
        status: task.status,
        rawTask: task,
      };
    });
  }, [tasks]);

  const formatText = (text) =>
    text && text.length > 15 ? `${text.slice(0, 15)}...` : text;

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "Task ID",
        cell: ({ getValue }) => getValue(),
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ getValue }) => formatText(getValue()),
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "requesterName",
        header: "Requester",
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "helperName",
        header: "Helper",
        meta: { headerProps: { className: "text-900" } },
      },
      {
        accessorKey: "status",
        header: "Status",
        meta: {
          headerProps: { className: "text-900" },
          cellProps: { className: "fs-9" },
        },
        cell: ({ getValue }) => {
          const statusRaw = getValue() || "UNKNOWN";
          const { label, color } = STATUS_MAP[statusRaw] || {
            label: statusRaw,
            color: "secondary",
          };
          return (
            <SubtleBadge pill bg={color}>
              {label}
            </SubtleBadge>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        meta: { headerProps: { className: "text-900" } },
        cell: ({ row }) => {
          const item = row.original;
          return (
            <Dropdown className="action-dropdown p-0">
              <Dropdown.Toggle
                as="button"
                className="bg-transparent border-0 p-0 shadow-none text-secondary"
              >
                ⋯
              </Dropdown.Toggle>
              <Dropdown.Menu className="action-menu">
                <Dropdown.Item onClick={() => handleViewTask(item.id)}>
                  View
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={() => handleDeactivateRequest(item)}
                  disabled={processing}
                >
                  Deactivate
                </Dropdown.Item>
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => handleDeleteRequest(item)}
                  disabled={processing}
                >
                  Delete
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          );
        },
      },
    ],
    [processing],
  );

  const table = useAdvanceTable({
    data: mappedTasks,
    columns,
    sortable: true,
    pagination: true,
    perPage: pageSize,
  });

  const triggerToast = useCallback((message, variant = "success") => {
    toast(message || (variant === "success" ? "Success" : "Error"), {
      type: variant,
      autoClose: 3500,
      closeButton: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, []);

  const handleViewTask = useCallback(
    async (id) => {
      try {
        await getTaskById(id);
        navigate(`/olitask/tasks/${id}`);
      } catch (err) {
        triggerToast("Failed to fetch task details", "error");
      }
    },
    [getTaskById, navigate, triggerToast],
  );

  const handleDeleteRequest = useCallback((task) => {
    setTaskPendingDelete(task);
  }, []);

  const handleCloseDeleteModal = useCallback(
    () => setTaskPendingDelete(null),
    [],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!taskPendingDelete) return;
    try {
      setProcessing(true);
      await deleteTask(taskPendingDelete.rawTask.id);
      triggerToast("Task deleted successfully", "success");
      handleCloseDeleteModal();
      fetchTasks(page, pageSize, currentTab?.searchTerm || "");
    } catch (err) {
      triggerToast(err?.message || "Failed to delete task", "error");
    } finally {
      setProcessing(false);
    }
  }, [
    deleteTask,
    taskPendingDelete,
    triggerToast,
    handleCloseDeleteModal,
    fetchTasks,
    page,
    pageSize,
    currentTab,
  ]);

  const handleDeactivateRequest = useCallback((task) => {
    setTaskPendingDeactivate(task);
  }, []);

  const handleCloseDeactivateModal = useCallback(
    () => setTaskPendingDeactivate(null),
    [],
  );

  const handleConfirmDeactivate = useCallback(async () => {
    if (!taskPendingDeactivate) return;
    try {
      setProcessing(true);
      await deactivateTask(taskPendingDeactivate.rawTask.id);
      triggerToast("Task deactivated successfully", "success");
      handleCloseDeactivateModal();
      fetchTasks(page, pageSize, currentTab?.searchTerm || "");
    } catch (err) {
      triggerToast(err?.message || "Failed to deactivate task", "error");
    } finally {
      setProcessing(false);
    }
  }, [
    deactivateTask,
    taskPendingDeactivate,
    triggerToast,
    handleCloseDeactivateModal,
    fetchTasks,
    page,
    pageSize,
    currentTab,
  ]);

  const handleTabChange = useCallback((tabKey) => {
    setPage(0);
    setActiveTab(tabKey);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setPage(0);
  }, []);

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spin animation="border" variant="primary" />
        {/* <span className="ms-3">Loading tasks...</span> */}
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="alert alert-danger d-flex align-items-center justify-content-between">
          <div>Error loading tasks: {error}</div>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() =>
              fetchTasks(page, pageSize, currentTab?.searchTerm || "")
            }
          >
            Retry
          </button>
        </div>
      </Container>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="poll"
        title="Task Management"
        subtitle="Manage and oversee all tasks within the OliTask platform."
        transform="shrink-2"
        className="mb-4 mt-3"
      />

      <Row className="mb-4 align-items-center">
        <Col className="d-flex align-items-center">
          {/* Left Arrow */}
          <button
            className="btn btn-link p-2 text-dark border-0"
            onClick={() => scroll("left")}
            style={{ zIndex: 10 }}
          >
            <ChevronLeft size={20} />
          </button>

          {/* Scrollable Tab Container */}
          <div
            ref={scrollRef}
            style={{
              overflowX: "auto",
              whiteSpace: "nowrap",
              scrollbarWidth: "none", // Hides scrollbar on Firefox
              msOverflowStyle: "none", // Hides scrollbar on IE/Edge
            }}
            className="flex-grow-1 no-scrollbar"
          >
            <Tabs
              activeKey={activeTab}
              onSelect={handleTabChange}
              className="flex-nowrap border-0"
              id="task-status-tabs"
              style={{ display: "inline-flex" }}
            >
              {TAB_CONFIG.map(({ key, label }) => (
                <Tab
                  key={key}
                  eventKey={key}
                  title={
                    <div className="position-relative px-3 py-2">{label}</div>
                  }
                />
              ))}
            </Tabs>
          </div>
          {/* Right Arrow */}
          <button
            className="btn btn-link p-2 text-dark border-0"
            onClick={() => scroll("right")}
            style={{ zIndex: 10 }}
          >
            <ChevronRight size={20} />
          </button>
        </Col>
        <div
          className="border-bottom"
          style={{ marginTop: "-1px", width: "100%" }}
        ></div>
      </Row>

      <AdvanceTableProvider {...table}>
        {tasks.length > 0 && (
          <Row className="mb-2">
            <Col md={4}>
              <AdvanceTableSearchBox />
            </Col>
            <Col md={8} className="text-end text-muted">
              {activeTab !== "ALL" && (
                <span className="text-xs">
                  Filtering by: {currentTab?.label}
                </span>
              )}
            </Col>
          </Row>
        )}

        {tasks.length === 0 ? (
          <Card className="mt-4 custom-shadow">
            <Card.Body className="text-center py-5">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <h5 className="text-muted mb-2 text-sm">
                      {activeTab === "ALL"
                        ? "No tasks found"
                        : `No ${currentTab?.label.toLowerCase()} tasks`}
                    </h5>
                    <p className="text-muted mb-0 text-xs">
                      {activeTab === "ALL"
                        ? "There are no tasks in the system yet."
                        : `There are no tasks with status "${currentTab?.label}" at the moment.`}
                    </p>
                  </div>
                }
              />
            </Card.Body>
          </Card>
        ) : (
          <>
            <AdvanceTable
              headerClassName="bg-200 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                bordered: true,
                striped: true,
                className: "fs-10 mb-0 overflow-hidden",
              }}
            />

            <div className="mt-3">
              <TablePaginationFooter
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                onPrev={() => handlePageChange(Math.max(page - 1, 0))}
                onNext={() =>
                  handlePageChange(Math.min(page + 1, totalPages - 1))
                }
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 30, 50, 100, 200, 500]}
              />
            </div>
          </>
        )}
      </AdvanceTableProvider>

      <DeleteTaskModal
        show={Boolean(taskPendingDelete)}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        task={taskPendingDelete?.rawTask}
        processing={processing}
      />

      <DeactivateTaskModal
        show={Boolean(taskPendingDeactivate)}
        onClose={handleCloseDeactivateModal}
        onConfirm={handleConfirmDeactivate}
        task={taskPendingDeactivate?.rawTask}
        processing={processing}
      />
    </>
  );
};

export default Tasks;
